import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { formatCLP, formatNumber, formatRut } from "@/lib/rut";
import { FileText, Download, Briefcase, TrendingUp, KeyRound, MessageSquare, Send, Inbox, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareholderDashboard() {
  const { user, setUser } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);
  const mustChange = !!user?.must_change_password;

  useEffect(() => {
    Promise.all([api.get("/purchases/me"), api.get("/announcements")])
      .then(([p, a]) => {
        setPurchases(p.data);
        setAnnouncements(a.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const totalAcciones = purchases.reduce((s, p) => s + (p.cantidad || 0), 0);
  const totalMonto = purchases.reduce((s, p) => s + (p.monto || 0), 0);

  const downloadPdf = async (id, filename) => {
    try {
      const token = localStorage.getItem("sn_token");
      const res = await fetch(`${API}/announcements/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) throw new Error("Sesión expirada. Vuelva a iniciar sesión.");
        if (res.status === 404) throw new Error("El archivo ya no está disponible.");
        throw new Error(`No se pudo descargar (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "comunicado.pdf";
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup so the browser has time to start the download
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1500);
    } catch (e) {
      toast.error(e.message || "Error al descargar");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="shareholder-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">
              Accionista · {formatRut(user?.rut)}
            </div>
            <h1 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-[#0A2540] tracking-tight">
              Hola, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-sm text-[#475569] mt-1">
              Aquí puede revisar su cartera y los comunicados oficiales.
            </p>
          </div>
          <Button
            data-testid="change-password-button"
            variant="outline"
            onClick={() => setPwOpen(true)}
            className="rounded-sm border-[#E2E8F0]"
          >
            <KeyRound size={16} className="mr-2" /> Cambiar contraseña
          </Button>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <KpiCard
            icon={<Briefcase size={18} />}
            label="Acciones totales"
            value={formatNumber(totalAcciones)}
            testid="kpi-total-acciones"
          />
          <KpiCard
            icon={<TrendingUp size={18} />}
            label="Monto invertido"
            value={formatCLP(totalMonto)}
            testid="kpi-total-monto"
          />
          <KpiCard
            icon={<FileText size={18} />}
            label="Comunicados disponibles"
            value={formatNumber(announcements.length)}
            testid="kpi-total-comunicados"
          />
        </div>

        <Tabs defaultValue="cartera" className="mt-10">
          <TabsList className="bg-white border border-[#E2E8F0] rounded-sm p-1">
            <TabsTrigger
              value="cartera"
              data-testid="tab-cartera"
              className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
            >
              Mi cartera
            </TabsTrigger>
            <TabsTrigger
              value="comunicados"
              data-testid="tab-comunicados"
              className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
            >
              Comunicados
            </TabsTrigger>
            <TabsTrigger
              value="solicitudes"
              data-testid="tab-solicitudes"
              className="rounded-sm data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
            >
              Solicitudes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cartera" className="mt-6">
            <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#0A2540]">
                    Detalle de compras
                  </h3>
                  <p className="text-xs text-[#475569]">
                    Registro histórico de sus compras de acciones.
                  </p>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">
                  {purchases.length} {purchases.length === 1 ? "registro" : "registros"}
                </div>
              </div>
              <Table data-testid="purchases-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Precio unit.</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Detalle</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#475569]">
                        Cargando…
                      </TableCell>
                    </TableRow>
                  ) : purchases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-[#475569]">
                        No hay compras registradas para su RUT.
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchases.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.fecha || "-"}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatNumber(p.cantidad)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-[#475569]">
                          {p.precio_unitario ? formatCLP(p.precio_unitario) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCLP(p.monto)}
                        </TableCell>
                        <TableCell className="text-[#475569] text-sm">
                          {p.detalle || "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="comunicados" className="mt-6">
            <div className="grid md:grid-cols-2 gap-4" data-testid="announcements-grid">
              {announcements.length === 0 ? (
                <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-sm p-10 text-center text-[#475569]">
                  No hay comunicados publicados.
                </div>
              ) : (
                announcements.map((a) => (
                  <div
                    key={a.id}
                    className="bg-white border border-[#E2E8F0] rounded-sm p-6 hover:-translate-y-0.5 transition-transform duration-150"
                  >
                    <div className="flex items-start justify-between">
                      <div className="h-10 w-10 rounded-sm bg-[#0A2540] text-white flex items-center justify-center">
                        <FileText size={18} />
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">
                        {new Date(a.uploaded_at).toLocaleDateString("es-CL")}
                      </div>
                    </div>
                    <h4 className="mt-4 font-heading text-lg font-semibold text-[#0A2540]">
                      {a.titulo}
                    </h4>
                    {a.descripcion && (
                      <p className="mt-1 text-sm text-[#475569]">{a.descripcion}</p>
                    )}
                    <div className="mt-5 flex items-center justify-between text-xs text-[#475569]">
                      <span>{(a.size / 1024).toFixed(0)} KB · PDF</span>
                      <Button
                        size="sm"
                        onClick={() => downloadPdf(a.id, a.filename)}
                        className="rounded-sm bg-[#0A2540] hover:bg-[#051626] text-white"
                        data-testid={`download-announcement-${a.id}`}
                      >
                        <Download size={14} className="mr-1" /> Descargar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="solicitudes" className="mt-6">
            <SolicitudesShareholder />
          </TabsContent>
        </Tabs>
      </div>

      <ChangePasswordDialog open={pwOpen || mustChange} onOpenChange={setPwOpen} forced={mustChange} onSuccess={() => setUser({ ...user, must_change_password: false })} />
    </div>
  );
}

function KpiCard({ icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#E2E8F0] rounded-sm p-6 hover:-translate-y-0.5 transition-transform duration-150"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569]">{label}</div>
        <div className="h-8 w-8 rounded-sm bg-[#F8F9FA] text-[#0A2540] flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="mt-4 font-heading text-3xl font-bold text-[#0A2540]">{value}</div>
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange, forced = false, onSuccess }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Contraseña actualizada");
      setCur("");
      setNw("");
      setConfirm("");
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (forced && !o) return; // prevent closing when forced
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="rounded-sm"
        data-testid="change-password-dialog"
        onInteractOutside={(e) => forced && e.preventDefault()}
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0A2540]">
            {forced ? "Establezca su nueva contraseña" : "Cambiar contraseña"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {forced
              ? "Debe cambiar su contraseña inicial antes de continuar."
              : "Actualice la contraseña de su cuenta."}
          </DialogDescription>
        </DialogHeader>
        {forced && (
          <div className="text-sm text-[#475569] bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm p-3">
            Por seguridad, debe cambiar su contraseña inicial antes de continuar.
            La contraseña actual son los primeros 4 caracteres de su correo (en minúsculas).
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-[#475569]">
              {forced ? "Contraseña inicial" : "Actual"}
            </Label>
            <Input
              type="password"
              required
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="rounded-sm h-11 border-[#E2E8F0]"
              data-testid="current-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-[#475569]">Nueva contraseña</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={nw}
              onChange={(e) => setNw(e.target.value)}
              className="rounded-sm h-11 border-[#E2E8F0]"
              data-testid="new-password-input"
            />
            <p className="text-[11px] text-[#475569]">Mínimo 6 caracteres.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-[#475569]">Repetir nueva contraseña</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-sm h-11 border-[#E2E8F0]"
              data-testid="confirm-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-sm bg-[#0A2540] hover:bg-[#051626]"
              data-testid="change-password-submit"
            >
              {loading ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SolicitudesShareholder() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/solicitudes/me")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!mensaje.trim()) return;
    setSending(true);
    try {
      await api.post("/solicitudes", { mensaje: mensaje.trim() });
      toast.success("Solicitud enviada");
      setMensaje("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const pendientes = items.filter((i) => i.estado === "pendiente").length;
  const respondidas = items.filter((i) => i.estado === "respondida").length;

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="solicitudes-shareholder">
      {/* Form */}
      <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-sm p-6 h-fit">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-[#0A2540]" />
          <h3 className="font-heading text-lg font-semibold text-[#0A2540]">
            Nueva solicitud
          </h3>
        </div>
        <p className="mt-1 text-xs text-[#475569]">
          Envíe su consulta directamente al equipo de administración. La respuesta aparecerá en el
          historial de abajo y quedará registrada en su perfil.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">
              Mensaje
            </Label>
            <Textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              rows={6}
              required
              placeholder="Describa su consulta…"
              className="rounded-sm border-[#E2E8F0]"
              data-testid="my-solicitud-mensaje-input"
            />
          </div>
          <Button
            type="submit"
            disabled={sending || !mensaje.trim()}
            className="w-full rounded-sm bg-[#0A2540] hover:bg-[#051626] text-white"
            data-testid="my-solicitud-submit"
          >
            {sending ? "Enviando…" : "Enviar solicitud"}
            <Send size={14} className="ml-2" />
          </Button>
        </form>
      </div>

      {/* History */}
      <div className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-sm p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox size={18} className="text-[#0A2540]" />
            <h3 className="font-heading text-lg font-semibold text-[#0A2540]">
              Historial de solicitudes
            </h3>
          </div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em]">
            <span className="border border-[#E2E8F0] px-2 py-1 rounded-sm text-[#D97706]">
              {pendientes} pendientes
            </span>
            <span className="border border-[#E2E8F0] px-2 py-1 rounded-sm text-[#16A34A]">
              {respondidas} respondidas
            </span>
          </div>
        </div>

        <div className="mt-5 space-y-3 max-h-[560px] overflow-auto" data-testid="my-solicitudes-list">
          {loading ? (
            <div className="text-center py-10 text-[#475569] text-sm">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#475569] text-sm border border-dashed border-[#E2E8F0] rounded-sm">
              Aún no ha enviado solicitudes.
            </div>
          ) : (
            items.map((s) => <SolicitudItem key={s.id} item={s} />)
          )}
        </div>
      </div>
    </div>
  );
}

function SolicitudItem({ item }) {
  const estadoStyle = {
    pendiente: "text-[#D97706] border-[#F59E0B]/30 bg-[#FFFBEB]",
    respondida: "text-[#16A34A] border-[#16A34A]/30 bg-[#F0FDF4]",
    descartada: "text-[#475569] border-[#E2E8F0] bg-[#F8F9FA]",
  }[item.estado] || "";

  return (
    <div
      className="border border-[#E2E8F0] rounded-sm p-4"
      data-testid={`my-solicitud-${item.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#475569]">
          {new Date(item.created_at).toLocaleDateString("es-CL")} ·{" "}
          {new Date(item.created_at).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 rounded-sm ${estadoStyle}`}
        >
          {item.estado}
        </span>
      </div>
      <p className="mt-2 text-sm text-[#0F172A] whitespace-pre-wrap">{item.mensaje}</p>
      {item.respuesta && (
        <div className="mt-3 border-t border-[#E2E8F0] pt-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#16A34A]">
            <CheckCircle2 size={12} /> Respuesta del administrador
          </div>
          <p className="mt-1.5 text-sm text-[#0F172A] whitespace-pre-wrap">{item.respuesta}</p>
          {item.responded_at && (
            <div className="mt-1 text-[10px] text-[#475569]">
              Respondida el {new Date(item.responded_at).toLocaleString("es-CL")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
